import {
    CameraLocationsUpdateEventHandler,
    LightsUpdateEventHandler,
    MaterialsUpdateEventHandler,
    MeshSelectedEventHandler,
    MeshesUpdateEventHandler,
    SceneSourceTaskCheckEventHandler
} from '@zakeke/zakeke-3d-core';
import React, { FC, useEffect, useId, useRef, useState } from 'react';
import { create } from 'zustand';
import styled from 'styled-components';
import { T } from 'src/shared/helpers';
import { DialogTitle, DialogWindow } from '../dialog';
import ModelMeasurementComponent from '../modelMeasurement/ModelMeasurementComponent';
import TryOnFineTuning from '../tryOnFineTuning';
import usePreview3DStore from './store';
import { TryOnProvider as LocalTryOnProvider, TryOnType as LocalTryOnType } from './tryOnTypes';

// DEEPAR NON SI PROVA DAL COMPUTER: il finetuning lo si fa col telefono, e quello che compare qui
// è un QR code da inquadrare. Gli altri provider portano la propria cornice — un iframe che si
// stende sul palco — mentre il QR code arrivava nudo e finiva IN FONDO alla colonna, sotto la
// tela: c'era, e non si vedeva.
//
// Nel vecchio backoffice stava in una finestra intitolata «Scan QR Code», ed è quella che torna. La
// finestra la mette chi monta il pannello, non il pannello: `vtoFinetuning` disegna il QR code e
// basta, ed è giusto così — la stessa cosa, nella procedura guidata del VTO, sta dentro una pagina
// e non dentro una finestra.
const QrCodeOverlay = styled.div`
    position: fixed;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 40px;
    overflow: auto;
    background-color: rgba(0, 0, 0, 0.7);

    /* Sopra la mano che il finetuning stende mentre carica (10000): quella è l'attesa di questo
       stesso pannello, non una cosa che gli sta davanti. */
    z-index: 10001;
`;

type SceneEditorViewProps = {
    /**
     * The variant whose material actions should be applied.
     * Null keeps the shared model as-is while still processing pending design updates.
     */
    variantId: number | null;
    isHighlightActive?: boolean;
    selectedMeshId?: string | null;
    isSelectionMode: boolean;
    onSelectionChanged?: (meshId: string | null) => void;
    onMeshesUpdated?: MeshesUpdateEventHandler;
    onMaterialsUpdated?: MaterialsUpdateEventHandler;
    onLightsUpdated?: LightsUpdateEventHandler;
    onCameraLocationsUpdated?: CameraLocationsUpdateEventHandler;
    onSceneTasksCheck?: SceneSourceTaskCheckEventHandler;
};

type SceneEditorState = {
    stack: string[];
    addCurrent: (id: string) => void;
    removeCurrent: (id: string) => void;
};

const useSceneEditorState = create<SceneEditorState>((set) => {
    return {
        stack: [],
        addCurrent: (id: string) =>
            set(({ stack }) => ({
                stack: [id, ...stack]
            })),

        removeCurrent: (id: string) =>
            set(({ stack }) => ({
                stack: stack.filter((x) => x !== id)
            }))
    };
});

const SceneEditorView: FC<SceneEditorViewProps> = ({
    onSelectionChanged,
    variantId,
    isHighlightActive,
    selectedMeshId,
    isSelectionMode,
    onMeshesUpdated,
    onMaterialsUpdated,
    onLightsUpdated,
    onCameraLocationsUpdated,
    onSceneTasksCheck
}) => {
    const id = useId();
    const containerRef = useRef<HTMLDivElement>(null);
    const onSelectionChangedRef = useRef(onSelectionChanged);
    const onMeshesUpdatedRef = useRef(onMeshesUpdated);
    const onMaterialsUpdatedRef = useRef(onMaterialsUpdated);
    const onLightsUpdatedRef = useRef(onLightsUpdated);
    const onCameraLocationsUpdatedRef = useRef(onCameraLocationsUpdated);
    const onSceneTasksCheckRef = useRef(onSceneTasksCheck);

    const viewerOnce = useSceneEditorState();
    const [sceneEditor, sceneManager, designManager, zkProduct, sceneRefreshKey, designUpdateRequest, clearDesignUpdateRequest] = usePreview3DStore((x) => [
        x.sceneEditor,
        x.sceneManager,
        x.designManager,
        x.zkProduct,
        x.sceneRefreshKey,
        x.designUpdateRequest,
        x.clearDesignUpdateRequest
    ]);

    // State per forzare re-render quando showFineTuning cambia
    const [showFineTuningState, setShowFineTuningState] = useState(false);

    // State per gestire le measurements
    const [showMeasurements, setShowMeasurements] = useState(false);
    const [modelMeasurements, setModelMeasurements] = useState<{ x: number; y: number; z: number } | null>(null);

    // Update refs
    onSelectionChangedRef.current = onSelectionChanged;
    onMeshesUpdatedRef.current = onMeshesUpdated;
    onMaterialsUpdatedRef.current = onMaterialsUpdated;
    onLightsUpdatedRef.current = onLightsUpdated;
    onCameraLocationsUpdatedRef.current = onCameraLocationsUpdated;
    onSceneTasksCheckRef.current = onSceneTasksCheck;

    const isActive = viewerOnce.stack.at(0) === id;
    const lastAppliedDesignRequestKeyRef = useRef(designUpdateRequest.key);

    const applyPendingDesignUpdate = async () => {
        if (!designManager || designUpdateRequest.key === lastAppliedDesignRequestKeyRef.current) return;

        lastAppliedDesignRequestKeyRef.current = designUpdateRequest.key;

        if (designUpdateRequest.hiddenMode !== undefined) {
            designManager.setHiddenMode(designUpdateRequest.hiddenMode);
        }

        if (designUpdateRequest.refresh && designUpdateRequest.variantId != null) {
            await designManager.refreshSides(designUpdateRequest.variantId);
        }

        clearDesignUpdateRequest(designUpdateRequest.key);
    };

    const updateColor = async () => {
        console.log(`Updating color for variantId ${variantId} in viewer ${id}`);
        if (variantId == null) {
            await applyPendingDesignUpdate();
            return;
        }

        const modelColor = zkProduct!.get('colors').find((x) => x.get('colorID') === variantId)!;

        // We can't use the classic selectAttribteValue in ComposerSelection because it requires a fully constructed
        // composer selection with all attributes and options, but the variant attribute could be created on the fly
        const attributeSelection = sceneManager!.getSelectedAttributeOption(modelColor);

        if (attributeSelection && attributeSelection.attributeGuid && attributeSelection.optionGuid) {
            const zkAttribute = zkProduct!.get('attributes').find((x) => x.get('attributeGuid') == attributeSelection.attributeGuid);

            if (zkAttribute) {
                const zkOption = zkAttribute.get('options').find((x) => x.get('optionGuid') == attributeSelection.optionGuid);

                if (zkOption) {
                    const actions = zkOption.get('actions').sortBy(function (item) {
                        return item.get('execOrder');
                    });

                    await sceneEditor!.executeActionsAsync(null, null, actions);
                    sceneEditor!.adjustCamera(false);
                } else throw `Option ${attributeSelection.optionGuid} not found`;
            } else throw `Attribute ${attributeSelection.attributeGuid} not found`;
        }

        const hasPendingDesignUpdate = designUpdateRequest.key !== lastAppliedDesignRequestKeyRef.current;
        await applyPendingDesignUpdate();

        if (!hasPendingDesignUpdate && !sceneManager!.hasVariantCustomModel(modelColor)) {
            await designManager?.refreshSides(variantId);
        }
    };

    useEffect(() => {
        viewerOnce.addCurrent(id);
        return () => viewerOnce.removeCurrent(id);
    }, [id]);

    useEffect(() => {
        if (!isActive) return;

        if (containerRef.current && sceneEditor) {
            sceneEditor.appendTo(containerRef.current);
            sceneEditor.updateView(true, true);
            updateColor();
        }

        // Event listeners
        const meshSelectedListener: MeshSelectedEventHandler = (e) => {
            console.log('Mesh selected: => la seleziono con scene editor', e.id);
            onSelectionChangedRef.current?.(e.id);
        };

        const meshesUpdateListener: MeshesUpdateEventHandler = (e) => {
            onMeshesUpdatedRef.current?.(e);
        };

        const materialsUpdateListener: MaterialsUpdateEventHandler = (e) => {
            onMaterialsUpdatedRef.current?.(e);
        };

        const lightsUpdateListener: LightsUpdateEventHandler = (e) => {
            onLightsUpdatedRef.current?.(e);
        };

        const cameraLocationsUpdateListener: CameraLocationsUpdateEventHandler = (e) => {
            onCameraLocationsUpdatedRef.current?.(e);
        };

        const sceneTasksCheckListener: SceneSourceTaskCheckEventHandler = (...args) => {
            onSceneTasksCheckRef.current?.(...args);
        };

        // add listeners al mount
        // questo è necessario per evitare che i listener vengano aggiunti più volte
        sceneEditor?.addMeshSelectedListener(meshSelectedListener);

        if (onMeshesUpdatedRef.current) {
            sceneEditor?.addMeshesUpdateListener(meshesUpdateListener);
        }

        if (onMaterialsUpdatedRef.current) {
            sceneEditor?.addMaterialsUpdateListener(materialsUpdateListener);
        }

        if (onLightsUpdatedRef.current) {
            sceneEditor?.addLightsUpdateListener(lightsUpdateListener);
        }

        if (onCameraLocationsUpdatedRef.current) {
            sceneEditor?.addCameraLocationsUpdateListener(cameraLocationsUpdateListener);
        }

        if (onSceneTasksCheckRef.current) {
            sceneEditor?.addSceneSourceTaskCheckListener(sceneTasksCheckListener);
        }

        return () => {
            // remove all listeners all' unmount
            sceneEditor?.removeMeshSelectedListener(meshSelectedListener);

            if (onMeshesUpdatedRef.current) {
                sceneEditor?.removeMeshesUpdateListener(meshesUpdateListener);
            }

            if (onMaterialsUpdatedRef.current) {
                sceneEditor?.removeMaterialsUpdateListener(materialsUpdateListener);
            }

            if (onLightsUpdatedRef.current) {
                sceneEditor?.removeLightsUpdateListener(lightsUpdateListener);
            }

            if (onCameraLocationsUpdatedRef.current) {
                sceneEditor?.removeCameraLocationsUpdateListener(cameraLocationsUpdateListener);
            }

            if (onSceneTasksCheckRef.current) {
                sceneEditor?.removeSceneSourceTaskCheckListener(sceneTasksCheckListener);
            }

            sceneEditor?.detach();
        };
    }, [containerRef.current, sceneEditor, isActive]);

    // React to variantId changes while the viewer is already mounted and active
    const prevVariantIdRef = useRef(variantId);
    useEffect(() => {
        if (!isActive || !sceneEditor || variantId === prevVariantIdRef.current) return;
        prevVariantIdRef.current = variantId;
        updateColor();
    }, [variantId, isActive, sceneEditor]);

    const prevSceneRefreshKeyRef = useRef(sceneRefreshKey);
    useEffect(() => {
        if (!isActive || !sceneEditor || sceneRefreshKey === prevSceneRefreshKeyRef.current) return;
        prevSceneRefreshKeyRef.current = sceneRefreshKey;
        updateColor();
    }, [sceneRefreshKey, isActive, sceneEditor]);

    const prevDesignUpdateRequestKeyRef = useRef(designUpdateRequest.key);
    useEffect(() => {
        if (!isActive || !sceneEditor || designUpdateRequest.key === prevDesignUpdateRequestKeyRef.current) return;
        prevDesignUpdateRequestKeyRef.current = designUpdateRequest.key;
        updateColor();
    }, [designUpdateRequest.key, isActive, sceneEditor]);

    useEffect(() => {
        if (isHighlightActive === undefined) return;

        sceneEditor?.setHighlightOver(isHighlightActive);
    }, [sceneEditor, isHighlightActive]);

    useEffect(() => {
        if (selectedMeshId === undefined) return;

        if (selectedMeshId === null) {
            sceneEditor?.unselectAllMeshes();
        } else {
            sceneEditor?.selectMesh(selectedMeshId);
        }
    }, [sceneEditor, selectedMeshId]);

    useEffect(() => {
        if (sceneEditor) sceneEditor.isSelectionMode = isSelectionMode;
    }, [sceneEditor, isSelectionMode]);

    // Effetto per monitorare e sincronizzare showFineTuning
    useEffect(() => {
        if (!sceneEditor) return;

        // Polling per monitorare showFineTuning (dato che potrebbe non essere osservabile)
        const interval = setInterval(() => {
            const currentShowFineTuning = sceneEditor.showFineTuning;
            if (currentShowFineTuning !== showFineTuningState) {
                console.log(`🔄 ShowFineTuning changed: ${showFineTuningState} -> ${currentShowFineTuning}`);
                setShowFineTuningState(currentShowFineTuning);
            }

            // Monitora anche le measurements
            const currentShowMeasurements = sceneEditor.showMeasurements;
            const currentModelMeasurements = sceneEditor.modelMeasurements;

            if (currentShowMeasurements !== showMeasurements) {
                console.log(`📏 ShowMeasurements changed: ${showMeasurements} -> ${currentShowMeasurements}`);
                setShowMeasurements(currentShowMeasurements);
            }

            if (currentShowMeasurements && currentModelMeasurements) {
                setModelMeasurements({
                    x: currentModelMeasurements.x,
                    y: currentModelMeasurements.y,
                    z: currentModelMeasurements.z
                });
            } else if (!currentShowMeasurements) {
                setModelMeasurements(null);
            }
        }, 100); // Check ogni 100ms

        return () => clearInterval(interval);
    }, [sceneEditor, showFineTuningState, showMeasurements]);

    // IL PROVIDER DECIDE CHE COSA COMPARE, e non ha un ripiego. Mazing e Banuba si accordano in
    // un iframe; DeepAR no — è un provider da telefono, e il suo finetuning è un QR code da
    // inquadrare. È `vtoFinetuning` a saperlo, ma solo se gli si dice QUALE provider: con un
    // ripiego a Mazing (`selectedProvider || 1`) un prodotto DeepAR apriva la cornice sbagliata,
    // e chi la guardava vedeva un iframe che non poteva funzionare.
    //
    // `selectedProvider` è di sola lettura e lo calcola il motore dalle impostazioni VTO della
    // scena: 0 = NotSelected. Senza provider non si mostra niente — è la stessa regola del
    // vecchio backoffice (`selectedProvider != TryOnProvider.NotSelected`).
    const hasValidProvider = !!sceneEditor && !!sceneEditor.selectedProvider;

    // Gestione del fine tuning - usa lo state locale invece della proprietà diretta
    const shouldShowFineTuning = !!sceneEditor && showFineTuningState && hasValidProvider;

    // Debug logging per vedere i valori
    React.useEffect(() => {
        if (sceneEditor) {
            const debugInfo = {
                showFineTuning: sceneEditor.showFineTuning,
                showFineTuningState,
                selectedProvider: sceneEditor.selectedProvider,
                hasValidProvider,
                shouldShow: shouldShowFineTuning,
                fineTuningModelLoaded: sceneEditor.fineTuningModelLoaded,
                selectedProviderFineTuningData: sceneEditor.selectedProviderFineTuningData,
                tryOnSettings: sceneEditor.getTryOnSettings?.()
            };
            console.log('🔍 SceneEditor fine tuning state:', debugInfo);

            // Log specifico per il rendering
            if (shouldShowFineTuning) {
                console.log('🟢 shouldShowFineTuning is TRUE - should render TryOnFineTuning');
            } else {
                console.log('🔴 shouldShowFineTuning is FALSE - TryOnFineTuning will NOT render');
            }
        }
    }, [sceneEditor?.showFineTuning, showFineTuningState, sceneEditor?.selectedProvider, shouldShowFineTuning, hasValidProvider]);

    const fineTuningData = shouldShowFineTuning
        ? {
              provider: sceneEditor.selectedProvider,
              mesh: sceneEditor.selectedProviderFineTuningData?.mesh,
              settings: sceneEditor.selectedProviderFineTuningData?.settings,
              modelLoaded: sceneEditor.fineTuningModelLoaded,
              type: sceneEditor.getTryOnSelectedType?.() ?? LocalTryOnType.NotSelected
          }
        : null;

    // Funzione per chiudere il fine tuning
    const handleCloseFIneTuning = () => {
        console.log('🔴 Closing Fine Tuning from SceneEditorView');
        if (sceneEditor) {
            // Chiama l'evento di chiusura del scene editor se disponibile
            if (sceneEditor.fireFineTuningCloseEvent) {
                sceneEditor.fireFineTuningCloseEvent();
            }
            // Assicurati che sia disabilitato
            sceneEditor.showFineTuning = false;
        }
    };

    // Funzione per resettare le measurements
    const resetMeasurements = () => {
        console.log('📏 Resetting measurements from timeout');
        setShowMeasurements(false);
        setModelMeasurements(null);
        if (sceneEditor) {
            // sceneEditor.showMeasurements = false;
        }
    };

    return (
        <div style={{ position: 'relative', width: '100%', height: '100%' }} ref={containerRef}>
            {/* Componente TryOnFineTuning integrato */}
            {(() => {
                if (!shouldShowFineTuning || !fineTuningData) return null;

                console.log('✅ Rendering TryOnFineTuning with data:', fineTuningData);

                const isQrCodeProvider = fineTuningData.provider === LocalTryOnProvider.DeepAR;

                // IL PANNELLO RICOMINCIA DA CAPO QUANDO CAMBIA IL PROVIDER. `vtoFinetuning` decide chi è
                // il provider al primo montaggio e poi se lo tiene: cambiando tipo VTO — da scarpe a
                // orecchini, cioè da DeepAR a Banuba — restava quello di prima, e continuava a mostrare
                // il QR code del modello precedente mentre gli si passava già il provider nuovo. La
                // chiave lo fa nascere di nuovo, che è la sola cosa che azzeri uno stato interno.
                const panel = (
                    <TryOnFineTuning
                        key={`${fineTuningData.provider}-${fineTuningData.type}`}
                        provider={fineTuningData.provider}
                        mesh={fineTuningData.mesh}
                        settings={fineTuningData.settings as any} // Cast per compatibilità tipi
                        modelLoaded={fineTuningData.modelLoaded}
                        type={fineTuningData.type}
                        onSettingsCallback={sceneEditor.fireFineTuningSettingEvent?.bind(sceneEditor)}
                        // Nella finestra del QR code a chiudere è la ✕ della finestra: due croci a due
                        // pixel di distanza sono una sola cosa detta due volte.
                        onCloseCallback={isQrCodeProvider ? undefined : handleCloseFIneTuning}
                    />
                );

                if (!isQrCodeProvider) return panel;

                return (
                    <QrCodeOverlay>
                        <DialogWindow size='medium' onClose={handleCloseFIneTuning}>
                            <DialogTitle>{T._('Scan QR Code', 'Admin')}</DialogTitle>
                            {panel}
                        </DialogWindow>
                    </QrCodeOverlay>
                );
            })()}

            {/* Componente ModelMeasurement integrato */}
            {showMeasurements && modelMeasurements && <ModelMeasurementComponent measures={modelMeasurements} onTimeout={resetMeasurements} timeout={5000} />}
        </div>
    );
};

export default SceneEditorView;
